/**
 * Navigation to hub (the Carbon6 platform shell).
 *
 * Ported from `frontend/src/utils/hub.ts`. PixelMe never treats hub as an OIDC
 * authority - Cognito is the only issuer. The browser is only ever navigated
 * here for platform logout and for onboarding.
 */
import { getHubBaseUrl } from '../auth/cognito';

const HUB_LOGOUT_PATH = '/api/cognito-auth/logout';

// There is deliberately no buildHubSignUpUrl here. Registration used to hop to
// a Hub `/signup` page first, on the reasoning that Terms/consent and UMS
// provisioning must not be bypassed. The approved onboarding architecture puts
// that guarantee somewhere else: signup goes to Cognito Managed Login like
// sign-in, and provisioning is enforced afterwards by the /api/v2/users/me
// readiness gate, which answers 409 HUB_REQUIRED with a hub_url pointing at
// /api/onboarding/enter until the user is actually provisioned. Hub never had
// a `/signup` page to receive the old hop, only /api/auth/signup, which is
// Hub's own front door and itself redirects to Cognito.

/**
 * Platform logout, routed through hub rather than straight at Cognito.
 *
 * Calling Cognito's end-session endpoint directly clears the hosted SSO cookie,
 * which is enough for PixelMe - our tokens live in per-tab storage and die with
 * it. It does nothing for hub. Hub's session is a 30-day httpOnly cookie on
 * hub's origin, read locally on every request without ever consulting Cognito,
 * and only a request to hub's own origin can clear it. Signing out of PixelMe
 * and then landing on the hub dashboard was the visible symptom.
 *
 * Hub's logout route clears that cookie, revokes the tokens pool-wide, and then
 * performs the Cognito end-session redirect itself - so one hop covers all
 * three, instead of us covering one of them.
 *
 * `returnTo` is what makes this viable: without it hub would strand the user on
 * its own login page. Hub validates the value against its product-origin
 * allowlist and passes it to Cognito as `logout_uri`, so PixelMe's origin must
 * also be registered as an allowed sign-out URL on the hub app client.
 */
export function buildHubLogoutUrl() {
  const url = new URL(HUB_LOGOUT_PATH, getHubBaseUrl());

  url.searchParams.set('returnTo', window.location.origin);

  return url.toString();
}

// Onboarding entry (`/api/onboarding/enter`) is not built here: the API hands
// it back in the `/api/v2/users/me` 409 HUB_REQUIRED response. It is
// validated and decorated in `lib/readiness.js`.
